/**
 * You can find more information about RichPlaceholder here:
 * https://adacado.atlassian.net/wiki/spaces/Inca/pages/575045644/Sample+Theme+zip+package+Folder+Structure
 */
const OUTPUT_MODE_EXAMPLE_VALUE = 'example-value'
const OUTPUT_MODE_PLACEHOLDER = 'placeholder'

const OUTPUT_MODES = [OUTPUT_MODE_EXAMPLE_VALUE, OUTPUT_MODE_PLACEHOLDER]

function insertPropertyIfDefined(targetObj, fieldName, fieldValue) {
  if (fieldValue === 0 || fieldValue === '' || fieldValue) {
    targetObj[fieldName] = fieldValue
  }
}

function invokeFunctOnPropertyIfDefined(targetObj, fieldName, transformationFunct) {
  if (targetObj.hasOwnProperty(fieldName)) {
    targetObj[fieldName] = transformationFunct.call(targetObj, targetObj[fieldName])
  }
}

/**
 * Build a rich-placeholder-object that can be inserted
 * into html-export, using value-token defined in some blueprint.
 *
 * "meta" is an optional input. It contains settings that define how
 * values should be sanitized. Currently it supports:
 * - isURL: pre-pend any missing http protocols
 *
 * @param {Object} tokenObject the value-token that can be mapped to a rich-placeholder-object
 * @param {Object} meta optional settings that might contain santization rules
 */
function buildRichPlaceholderObject(tokenObject, meta = {}) {
  //General required fields
  const {
    name,
    displayName,
    type,
    codeType,
    inventoryIndex,
    isAdvertiserData,
    dataField,
    onFalse,
    onTrue,
    version,
    autoFormat,
    autoFormatConfigCurrencySymbol,
    autoFormatConfigSeparator,
    autoFormatConfigRound,
  } = tokenObject
  const renderObject = {
    name,
    displayName,
    type,
    codeType,
    inventoryIndex,
    isAdvertiserData,
    dataField,
    onFalse,
    onTrue,
    version,
    autoFormat,
    autoFormatConfigCurrencySymbol,
    autoFormatConfigSeparator,
    autoFormatConfigRound,
  }

  // optional fields
  ;['truncate', 'defaultValue'].forEach(optionalFieldName => {
    insertPropertyIfDefined(renderObject, optionalFieldName, tokenObject[optionalFieldName])
  })

  //apply "escape" to avoid special HTML characters
  invokeFunctOnPropertyIfDefined(renderObject, 'defaultValue', escape)

  //check if we need to prepend "http" protocol when inserting into a href attribute
  if (meta.isURL) {
    invokeFunctOnPropertyIfDefined(renderObject, 'defaultValue', prependHttp)
  }

  return renderObject
}

//FIXME: Find a way to apply data-sanitzation only once through the lifecylce
function escape(rawString) {
  if (typeof rawString !== 'string') {
    return rawString
  }

  return rawString.replace(/([<>]|{{|}})/g, substr => {
    return (
      {
        '<': '&lt;',
        '>': '&gt;',
        '{{': '&#x7B;&#x7B;',
        '}}': '&#x7D;&#x7D;',
      }[substr] || substr
    )
  })
}

function prependHttp(rawString) {
  if (typeof rawString !== 'string' || !rawString) {
    return rawString
  }

  // make sure the string is trimmed before looking for https protocol
  const trimmed = rawString.trim()

  // check if the given string is missing https
  // note that this is case-insensitive search
  if (!/^https?/i.test(trimmed)) {
    return 'https://' + trimmed
  }

  return rawString
}

// Handle truncating of string type rich placeholders
function truncateString(str, ln) {
  if (typeof str !== 'string') {
    return str
  }

  // Do nothing if there no truncate limit or limit is larger than text
  if (!ln || ln > str.length) {
    return str
  }

  // Trim to max allowable length
  const newStr = str.substr(0, ln)

  // Re-trim if we are in the middle of a word
  const retrim = newStr.substr(0, Math.min(newStr.length, newStr.lastIndexOf(' ')))

  // Add an ellipsis
  return `${retrim || newStr}...`
}

export default class {
  constructor(options = {}) {
    const { mode } = options

    this.mode = mode

    // validation
    if (!OUTPUT_MODES.includes(mode)) {
      this.mode = OUTPUT_MODE_EXAMPLE_VALUE
    }
  }

  /**
   * Compile the given value "token" into its appropriate literal values
   *
   * "meta" is an optional input. It contains settings that define how
   * values should be sanitized. Currently it supports:
   * - isURL: pre-pend any missing http protocols
   *
   * @param {Object} tokenData a value-token object
   * @param {Object} meta optional settings that might contain santization rules
   */
  compile(tokenData, meta = {}) {
    // treat any non-Object type as raw data
    // we can simply return it as is
    if (typeof tokenData !== 'object') {
      //FIXME: we need to know whether the content is inserted
      // as HTML, script, or attribute.
      //For now, we assume token-data contains NO HTML tags....
      let result = escape(tokenData)

      if (meta.isURL) {
        result = prependHttp(result)
      }

      return result
    }
    // for all other cases, the tokenData is an Object...
    //  we will treat that as a Rich-Placeholder

    // when displaying the value-token in template-editor (design-time)
    if (this.mode === OUTPUT_MODE_EXAMPLE_VALUE) {
      let result = tokenData.defaultValue || ''
      // ensure to display truncated texts for ace-editor
      if (tokenData.truncate) {
        result = truncateString(result, tokenData.truncate)
      }
      return result
    }

    // when displaying the value-token in actual ad (run-time)
    if (this.mode === OUTPUT_MODE_PLACEHOLDER) {
      const renderObject = buildRichPlaceholderObject(tokenData, meta)
      return '{' + JSON.stringify(renderObject) + '}'
    }

    // return empty string for other unknown cases
    return ''
  }

  /**
   * Compile the given value "token" into its appropriate image-asset URL
   *
   * "imageResizeParameters" is required when we are generating output for export-HTML,
   *   that is going to be rendered in actual ad
   *
   * @param {Object} tokenData a value-token object
   * @param {Object} imageResizeParameters an object containg resize and clip paramaters
   */
  compileImageSource(tokenData, imageResizeParameters, widget) {
    const isString = typeof tokenData === 'string'
    const isObject = typeof tokenData === 'object'

    if (!isString && !isObject) {
      return ''
    }
    if (isString && tokenData.trim().length === 0) {
      return ''
    }

    // when displaying the value-token in template-editor (design-time)
    if (this.mode === OUTPUT_MODE_EXAMPLE_VALUE) {
      let url = isString ? tokenData : tokenData.defaultValue || '' // sanity check in case defaultValue is undefined

      if (url.trim().length === 0) {
        return ''
      }

      return url
    }

    // when displaying the value-token in actual ad (run-time)
    if (this.mode === OUTPUT_MODE_PLACEHOLDER) {
      // image-resize-parameters is required for this mode
      if (!imageResizeParameters) {
        return ''
      }

      const { width, height, cropAreaX1, cropAreaX2, cropAreaY1, cropAreaY2, valign, halign } = imageResizeParameters

      const filePath = isString
        ? v2Encoding(tokenData)
        : '{' + JSON.stringify(buildRichPlaceholderObject(tokenData)) + '}'
      return `/image-resize/v3/crop/\
${halign}/${valign}/\
${width}/${height}/\
${cropAreaX1}/${cropAreaX2}/\
${cropAreaY1}/${cropAreaY2}/\
${widget.baseStyle?.vertical || 0}/\
${widget.baseStyle?.horizontal || 0}/\
${widget.layout?.zRotation || 0}/\
${filePath}\
`
    }

    // return empty string for all other cases
    return ''
  }
}

function v2Encoding(str) {
  const splitUrl = str.split('/')
  const filename = splitUrl[splitUrl.length - 1]
  if (str.includes('abstractQuickStartTemplatePath')) return `encodedAbstractQuickStartPath/${filename}`
  return `encodedAbstractPath/${filename}`
}
